import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { CityRouteDetail } from "../../../mocks/routeDetails";
import RouteStopTimeline from "./RouteStopTimeline";

interface RouteCardItemProps {
  route: CityRouteDetail;
  index: number;
}

const DIFFICULTY_CONFIG = {
  easy:   { label: "Kolay",  class: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
  medium: { label: "Orta",   class: "bg-amber-100 text-amber-700",    dot: "bg-amber-500" },
  hard:   { label: "Zorlu",  class: "bg-red-100 text-red-600",        dot: "bg-red-500" },
};

export default function RouteCardItem({ route, index }: RouteCardItemProps) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const diff = DIFFICULTY_CONFIG[route.difficulty];

  const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=40.18352,29.06098`;

  return (
    <article
      className={`bg-white rounded-2xl overflow-hidden border border-stone-100 transition-all duration-300 ${expanded ? "border-emerald-200" : "hover:border-stone-200"}`}
    >
      {/* Card Top: Image + Overview */}
      <div
        className={`grid grid-cols-1 ${index % 2 === 0 ? "md:grid-cols-[2fr_3fr]" : "md:grid-cols-[3fr_2fr]"}`}
      >
        {/* Image side */}
        <div
          className={`relative overflow-hidden group cursor-pointer ${index % 2 !== 0 ? "md:order-2" : ""}`}
          style={{ minHeight: "280px" }}
          onClick={() => setExpanded(!expanded)}
        >
          <img
            src={route.image}
            alt={route.title}
            className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105 absolute inset-0"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 via-transparent to-transparent" />

          {/* Overlay stats */}
          <div className="absolute bottom-4 left-4 flex items-center gap-2">
            <span className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${diff.class}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${diff.dot}`} />
              {diff.label}
            </span>
            <span className="px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm text-white text-xs font-medium">
              {route.category}
            </span>
          </div>

          {/* Expand indicator */}
          <div className="absolute top-4 right-4">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm text-white transition-transform duration-300" style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}>
              <i className="ri-arrow-down-s-line text-lg" />
            </div>
          </div>

          {/* Route number badge */}
          <div className="absolute top-4 left-4 w-9 h-9 flex items-center justify-center rounded-full bg-emerald-700 text-white font-bold text-sm">
            {String(index + 1).padStart(2, "0")}
          </div>
        </div>

        {/* Content side */}
        <div className={`p-6 md:p-8 flex flex-col ${index % 2 !== 0 ? "md:order-1" : ""}`}>
          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-3">
            {route.tags.slice(0, 3).map((tag) => (
              <span key={tag} className="px-2.5 py-0.5 bg-stone-100 text-stone-500 rounded-full text-xs font-medium">
                {tag}
              </span>
            ))}
          </div>

          <h2 className="font-playfair text-2xl font-bold text-stone-900 mb-2">{route.title}</h2>
          <p className="text-stone-500 text-sm leading-relaxed mb-4 flex-1">{route.longDescription}</p>

          {/* Meta row */}
          <div className="flex flex-wrap gap-4 mb-5">
            <div className="flex items-center gap-1.5 text-sm text-stone-500">
              <span className="w-6 h-6 flex items-center justify-center">
                <i className="ri-time-line text-emerald-600" />
              </span>
              <strong className="text-stone-700">{route.durationHours} saat</strong>
            </div>
            <div className="flex items-center gap-1.5 text-sm text-stone-500">
              <span className="w-6 h-6 flex items-center justify-center">
                <i className="ri-map-pin-2-line text-emerald-600" />
              </span>
              <strong className="text-stone-700">{route.stops.length} durak</strong>
            </div>
            <div className="flex items-center gap-1.5 text-sm text-stone-500">
              <span className="w-6 h-6 flex items-center justify-center">
                <i className="ri-road-map-line text-emerald-600" />
              </span>
              <strong className="text-stone-700">~{route.totalDistanceKm} km</strong>
            </div>
          </div>

          {/* Ideal for */}
          <div className="mb-5">
            <span className="text-xs text-stone-400 uppercase tracking-wider block mb-2">Kimler İçin İdeal</span>
            <div className="flex flex-wrap gap-1.5">
              {route.idealFor.map((item) => (
                <span key={item} className="flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full">
                  <i className="ri-check-line text-xs" /> {item}
                </span>
              ))}
            </div>
          </div>

          {/* Transport note */}
          <div className="flex items-start gap-2 text-xs text-stone-500 bg-stone-50 rounded-xl p-3 mb-5">
            <i className="ri-car-line text-stone-400 mt-0.5 flex-shrink-0 text-sm" />
            <span>{route.transportNote}</span>
          </div>

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              onClick={() => setExpanded(!expanded)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                expanded
                  ? "bg-stone-100 text-stone-600 hover:bg-stone-200"
                  : "bg-emerald-700 text-white hover:bg-emerald-800"
              }`}
            >
              <i className={`${expanded ? "ri-eye-off-line" : "ri-list-check-2"} text-base`} />
              {expanded ? "Rotayı Gizle" : "Rotayı İncele"}
            </button>
            <a
              href={mapsUrl}
              target="_blank"
              rel="nofollow noreferrer"
              className="flex items-center gap-2 border border-stone-200 text-stone-600 rounded-xl px-4 py-2.5 text-sm font-medium hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-navigation-line text-base" />
              Başlat
            </a>
          </div>
        </div>
      </div>

      {/* Expandable Stops Section */}
      {expanded && (
        <div className="border-t border-stone-100">
          <div className="p-6 md:p-8">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
              <h3 className="font-semibold text-stone-800 text-base flex items-center gap-2">
                <i className="ri-route-line text-emerald-600" />
                Adım Adım Rota — {route.stops.length} Durak
              </h3>
              <div className="flex gap-2">
                {route.stops.map((_, i) => (
                  <div
                    key={i}
                    className="w-6 h-1.5 rounded-full bg-emerald-200 overflow-hidden"
                  >
                    <div className="h-full bg-emerald-600 rounded-full" style={{ width: "100%" }} />
                  </div>
                ))}
              </div>
            </div>

            {/* Timeline */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12">
              <div>
                <RouteStopTimeline stops={route.stops.slice(0, Math.ceil(route.stops.length / 2))} />
              </div>
              {route.stops.length > 1 && (
                <div>
                  <RouteStopTimeline stops={route.stops.slice(Math.ceil(route.stops.length / 2))} />
                </div>
              )}
            </div>

            {/* Bottom CTA */}
            <div className="mt-6 pt-6 border-t border-stone-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="text-sm text-stone-500">
                <strong className="text-stone-700">Toplam süre:</strong> ~{route.durationHours} saat &nbsp;·&nbsp;
                <strong className="text-stone-700">Mesafe:</strong> ~{route.totalDistanceKm} km
              </div>
              <div className="flex gap-3">
                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl px-5 py-2.5 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-navigation-line" /> Google Maps'te Aç
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </article>
  );
}
